import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { QRCodeComponent } from 'angularx-qrcode';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { MatIconButtonComponent } from '../../_components/buttons/mat-icon-button/mat-icon-button.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { MatOutlinedButtonComponent } from '../../_components/buttons/mat-outlined-button/mat-outlined-button.component';
import { MatIcon } from '@angular/material/icon';
import { ErrorCode, ErrorObject } from 'src/app/_models/error';
import { UserService } from '../../_services/user.service';
import { TwoFactorSetupResponse } from 'src/app/_models/2fa';
import { Checkbox } from 'src/app/_models/checkbox';
import { CheckboxComponent } from '../../_components/selection-controls/checkbox/checkbox.component';
import { LoadingSpinnerComponent } from '../../_components/loading-spinner/loading-spinner.component';
import { handleDigitFocus, handleDigitInput, handleDigitKeydown, handleDigitPaste } from 'src/app/_helpers/utils';

@Component({
  selector: 'app-2fa-enrollment',
  templateUrl: './2fa-enrollment.component.html',
  styleUrl: './2fa-enrollment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgOptimizedImage,
    MatIconButtonComponent,
    ButtonComponent,
    ReactiveFormsModule,
    QRCodeComponent,
    MatOutlinedButtonComponent,
    MatIcon,
    CheckboxComponent,
    LoadingSpinnerComponent,
  ],
})
export class TwoFaEnrollmentComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isProcessing = signal(false);
  readonly isInitialLoad = signal(true);
  readonly error = signal<ErrorObject | null>(null);
  readonly clipboardError = signal<string | null>(null);
  readonly view = signal<
    'enrollment' | 'recovery-codes' | 'already-enrolled' | 'org-pending-2fa'
  >('enrollment');
  private readonly orgRedirectUrl = signal<string | null>(null);
  private readonly orgRedirectPending = computed(() => this.orgRedirectUrl() !== null);

  sharedKey = signal<string | null>(null);
  recoveryCodes = signal<string[] | null>(null);
  qrCodeUrl = signal<string | null>(null);

  readonly digitInputs =
    viewChildren<ElementRef<HTMLInputElement>>('digitInput');

  // Only the heading of the currently rendered view carries this ref, so the
  // signal changes exactly when the user is moved to a different view.
  private readonly viewHeading =
    viewChild<ElementRef<HTMLHeadingElement>>('viewHeading');

  // Views swap in place without a route change, so the control the user was on
  // is destroyed and focus falls back to <body> — keyboard users would restart
  // from the top of the document and screen readers would announce nothing.
  // Moving focus to the new heading keeps their place and announces the change.
  private readonly focusHeadingOnViewChange = effect(() => {
    this.viewHeading()?.nativeElement.focus();
  });

  readonly savedRecoveryCodes = new Checkbox(
    'savedRecoveryCodes',
    'I have saved my recovery codes in a secure location',
    'done',
  );

  readonly confirmationCodeForm = this.formBuilder.group({
    digit0: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit1: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit2: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit3: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit4: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    digit5: ['', [Validators.required, Validators.pattern(/^\d$/)]],
  });

  readonly recoveryCodeForm = this.formBuilder.group({
    [this.savedRecoveryCodes.id]: [false, Validators.requiredTrue],
  });

  private get digitControls() {
    const { digit0, digit1, digit2, digit3, digit4, digit5 } =
      this.confirmationCodeForm.controls;
    return [digit0, digit1, digit2, digit3, digit4, digit5];
  }

  ngOnInit(): void {
    try {
      // `postEnrollmentRedirect` is written by ErrorInterceptor into sessionStorage, so
      // treat it as untrusted and validate before handing it to the router. This is a
      // literal-prefix allowlist, not a sanitiser — relaxing it to startsWith('/') to
      // support other destinations would admit protocol-relative URLs like //evil.com.
      const redirect = sessionStorage.getItem('postEnrollmentRedirect');
      if (redirect?.startsWith('/organisations') && !redirect.includes('://')) {
        this.orgRedirectUrl.set(redirect);
      }
    } catch (e) {
      // sessionStorage blocked (e.g. browser strict tracking protection, enterprise policy).
      // Enrollment still proceeds; the org redirect will not trigger.
      console.error('[2fa-enrollment] sessionStorage inaccessible, org redirect suppressed.', e);
    }
    this.getQrCode();
  }

  onRecoveryCodesDone(): void {
    if (this.orgRedirectPending()) {
      this.view.set('org-pending-2fa');
    } else {
      this.goToDashboard();
    }
  }

  copyCode(): void {
    const code = this.sharedKey();
    if (!code) return;
    this.clipboardError.set(null);
    navigator.clipboard.writeText(code).catch(() => {
      this.clipboardError.set('Failed to copy — please copy the key manually.');
    });
  }

  onDigitFocus(event: FocusEvent): void {
    handleDigitFocus(event);
  }

  onDigitInput(index: number, event: Event): void {
    handleDigitInput(index, event, this.digitInputs());
  }

  onDigitKeydown(event: KeyboardEvent, index: number): void {
    handleDigitKeydown(event, index, this.digitInputs(), this.digitControls);
  }

  onPaste(event: ClipboardEvent): void {
    handleDigitPaste(event, this.digitInputs(), this.digitControls);
  }

  getQrCode(): void {
    this.error.set(null);
    this.userService
      .setupTwoFactor()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: TwoFactorSetupResponse) => {
          this.isInitialLoad.set(false);
          this.qrCodeUrl.set(response.otpauthUri);
          this.sharedKey.set(response.sharedKey);
        },
        error: (error: ErrorObject) => {
          this.isInitialLoad.set(false);
          if (error.code === ErrorCode.TwoFactorAlreadyEnabled) {
            this.authService.patchCurrentUser({ twoFactorSetupRequired: false, twoFactorEnabled: true });
            this.error.set(error);
            this.view.set('already-enrolled');
            return;
          }
          this.error.set(error);
        },
        complete: () => this.isInitialLoad.set(false),
      });
  }

  handleSubmit(): void {
    if (this.isProcessing()) return;

    this.isProcessing.set(true);
    this.error.set(null);

    const code = this.digitControls.map((c) => c.value).join('');

    this.userService
      .enableTwoFactor(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isProcessing.set(false);
          this.authService.patchCurrentUser({ twoFactorSetupRequired: false, twoFactorEnabled: true });
          if (!response?.recoveryCodes?.length) {
            this.error.set({ status: 0, message: 'Two-factor authentication is enabled, but we couldn\'t generate your recovery codes. Please go to Account Settings > Two-Factor Authentication to generate new recovery codes before you rely on 2FA to sign in.', code: null });
            this.view.set('already-enrolled');
            return;
          }
          this.recoveryCodes.set(response.recoveryCodes);
          this.view.set('recovery-codes');
        },
        error: (error: ErrorObject) => {
          this.isProcessing.set(false);
          if (error.code === ErrorCode.RecoveryCodeGenerationFailed || error.code === ErrorCode.TwoFactorAlreadyEnabled) {
            this.authService.patchCurrentUser({ twoFactorSetupRequired: false, twoFactorEnabled: true });
            this.error.set(error);
            this.view.set('already-enrolled');
            return;
          }
          this.error.set(error);
        },
        complete: () => this.isProcessing.set(false),
      });
  }

  goToDashboard(): void {
    try {
      sessionStorage.removeItem('postEnrollmentRedirect');
    } catch {
      // Storage blocked; cleanup skipped, navigation proceeds.
    }
    void this.router.navigate(['/dashboard']);
  }

  goToOrganisation(): void {
    const url = this.orgRedirectUrl();
    if (!url) {
      this.goToDashboard();
      return;
    }
    try {
      sessionStorage.removeItem('postEnrollmentRedirect');
    } catch {
      // Storage blocked; cleanup skipped, navigation proceeds.
    }
    void this.router.navigate([url]);
  }

  copyRecoveryCodes(): void {
    const codes = this.recoveryCodes();
    if (!codes?.length) return;
    this.clipboardError.set(null);
    navigator.clipboard.writeText(codes.join('\n')).catch(() => {
      this.clipboardError.set('Failed to copy — please select and copy the codes manually.');
    });
  }
}
